/*
 * The repeatable story beat.
 *
 * One sequence, in order: the heading takes the masked reveal, then the
 * sub-heading, body and button fade up one after another, and the media plays
 * its own entrance alongside the heading.
 *
 * Every element's hidden state is applied by the helpers the moment the scene
 * is built, so nothing is readable before its turn — the timeline only animates
 * out of that state once the trigger fires.
 */
import { defineScene, revealText, riseIn, reducedMotion, enterConfig } from '@theme/about-story';

/**
 * Starting offsets for the media, keyed by the section's `media_motion`
 * setting. Each is a state to animate *out of*, back to the neutral values.
 */
const MEDIA_FROM = {
	'rise-rotate': (fromLeft) => ({
		autoAlpha: 0,
		y: 140,
		rotation: fromLeft ? -14 : 14,
		scale: 0.88,
		transformOrigin: '50% 100%',
	}),
	'rise-scale': () => ({ autoAlpha: 0, y: 90, scale: 1.08 }),
	'slide-in': (fromLeft) => ({
		autoAlpha: 0,
		x: fromLeft ? -180 : 180,
		xPercent: fromLeft ? -8 : 8,
	}),
};

const MEDIA_TO = { autoAlpha: 1, y: 0, x: 0, xPercent: 0, rotation: 0, scale: 1 };

/* A held beat before anything moves, so the reveal is caught mid-flight rather
   than looking like it happened before the section arrived. */
const START = 0.15;

defineScene('about-beat', (root, gsap) => {
	const heading = root.querySelector('[data-about-heading]');
	const rising = root.querySelectorAll('[data-about-rise]');
	const media = root.querySelector('[data-about-media]');
	const granularity = root.dataset.granularity === 'words' ? 'words' : 'lines';
	const motion = root.dataset.mediaMotion;
	const fromLeft = root.dataset.mediaPosition === 'left';

	const timeline = gsap.timeline({
		...enterConfig(heading ?? root, Number(root.dataset.revealStart) || 75),
	});

	if (heading) revealText(heading, { timeline, granularity, position: START });

	/* '>' is the end of the heading reveal, so the supporting copy follows it
	   rather than riding along underneath — then staggers sub, body, button. */
	if (rising.length) riseIn(rising, { timeline, position: heading ? '>' : START });

	if (!media) return;

	const build = MEDIA_FROM[motion] ?? MEDIA_FROM['rise-scale'];
	const duration = motion === 'rise-rotate' ? 1.3 : 1.1;

	gsap.set(media, reducedMotion() ? { autoAlpha: 0 } : build(fromLeft));
	timeline.to(
		media,
		reducedMotion()
			? { autoAlpha: 1, duration: 0.4 }
			: { ...MEDIA_TO, duration, ease: 'power3.out' },
		START + 0.1
	);
});
